import { useState, useCallback, useEffect } from 'react';
import {
  tokens,
  makeStyles,
  shorthands,
  Card,
  Text,
  Input,
  Button,
  Spinner,
  Field,
  Switch,
  Tag,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Divider,
} from '@fluentui/react-components';
import {
  MailRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ArrowResetRegular,
  SendRegular,
  UsbPlugRegular,
  ShieldKeyholeRegular,
  CloudArrowUpRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import type { EntraUserDetails, AdminIdentity, ProvisioningResult, SecurityAlertDetails } from '../../types';
import { SECURITY_ALERT_RECIPIENT, ENTRA_ADMIN_USER_URL_PREFIX } from '../../constants';
import {
  sendSecurityAlertEmail,
  sendPinNotificationEmail,
  getFido2CreationOptions,
  registerFido2Method,
} from '../../services/graph';
import { createFido2Credential, isWebAuthnSupported } from '../../services/webauthn';

const useStyles = makeStyles({
  card: {
    width: '100%',
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('12px'),
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    border: 'none',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  stepBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorBrandBackground,
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  cardSubtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  keyInfoBox: {
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('10px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  keyInfoHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  keyInfoHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '18px',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
  },
  checkIcon: {
    flexShrink: 0,
  },
  notificationBox: {
    ...shorthands.padding('16px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('10px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  notifHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  notifHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '4px',
  },
  provisionBtn: {
    minWidth: '200px',
    height: '40px',
    fontWeight: 600,
    ...shorthands.borderRadius('8px'),
  },
  // Progress substeps
  progressBox: {
    ...shorthands.padding('20px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('12px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  progressStep: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
  },
  progressIcon: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  progressActive: {
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  progressDone: {
    color: tokens.colorPaletteGreenForeground1,
  },
  progressPending: {
    color: tokens.colorNeutralForeground4,
  },
  touchPrompt: {
    ...shorthands.padding('16px', '20px'),
    backgroundColor: tokens.colorPaletteYellowBackground1,
    ...shorthands.borderRadius('10px'),
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorPaletteYellowForeground2,
  },
  // Result styles
  successCard: {
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('12px'),
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  successHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
  },
  successIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: '#fff',
    fontSize: '20px',
    flexShrink: 0,
  },
  resultDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
  },
  resultLabel: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 500,
  },
  resultValue: {
    color: tokens.colorNeutralForeground1,
    fontFamily: "'Cascadia Code', Consolas, monospace",
    fontSize: '12px',
  },
  emailStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    ...shorthands.padding('4px', '0'),
  },
});

interface ProvisioningFormProps {
  targetUser: EntraUserDetails;
  pin: string;
  admin: AdminIdentity;
  onReset: () => void;
  onProvisioned?: () => void;
}

type ProvisioningStatus =
  | 'idle'
  | 'getting-options'
  | 'waiting-for-key'
  | 'registering'
  | 'sending-alert'
  | 'complete'
  | 'error';

const STATUS_LABELS: Record<ProvisioningStatus, string> = {
  'idle': 'Provision FIDO2 Key',
  'getting-options': 'Getting challenge...',
  'waiting-for-key': 'Touch your security key...',
  'registering': 'Registering with Entra ID...',
  'sending-alert': 'Sending notifications...',
  'complete': 'Complete',
  'error': 'Retry Provisioning',
};

export function ProvisioningForm({ targetUser, pin, admin, onReset, onProvisioned }: ProvisioningFormProps) {
  const styles = useStyles();
  const [keyDisplayName, setKeyDisplayName] = useState(
    `Security Key - ${new Date().toISOString().slice(0, 10)}`
  );
  const [notes, setNotes] = useState('');
  const [sendPinEmail, setSendPinEmail] = useState(false);
  const [pinRecipientEmail, setPinRecipientEmail] = useState(targetUser.mail || targetUser.userPrincipalName);
  const [status, setStatus] = useState<ProvisioningStatus>('idle');
  const [result, setResult] = useState<ProvisioningResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [alertEmailSent, setAlertEmailSent] = useState(false);
  const [pinEmailSent, setPinEmailSent] = useState(false);
  const [emailErrors, setEmailErrors] = useState<string[]>([]);

  const [webauthnSupported, setWebauthnSupported] = useState(true);

  // Check WebAuthn support on mount
  useEffect(() => {
    setWebauthnSupported(isWebAuthnSupported());
  }, []);

  const isActive = status !== 'idle' && status !== 'complete' && status !== 'error';

  const handleProvision = useCallback(async () => {
    setStatus('getting-options');
    setError(null);
    setEmailErrors([]);
    setAlertEmailSent(false);
    setPinEmailSent(false);

    const correlationId = crypto.randomUUID();
    const timestamp = new Date().toISOString();

    try {
      // Step 1: Get WebAuthn creation options from Graph beta (must use UPN, not UUID)
      const creationOptions = await getFido2CreationOptions(targetUser.userPrincipalName);

      // Step 2: Create credential on the USB security key via CTAP2 over WebHID
      setStatus('waiting-for-key');
      const webauthnResult = await createFido2Credential(creationOptions, pin);

      // Step 3: Register the credential with Entra ID
      setStatus('registering');
      const registration = await registerFido2Method(
        targetUser.id,
        keyDisplayName,
        webauthnResult
      );

      const provisioningResult: ProvisioningResult = {
        success: true,
        correlationId,
        timestamp,
        targetUserId: targetUser.id,
        targetUserDisplayName: targetUser.displayName,
        targetUserUpn: targetUser.userPrincipalName,
        keyDisplayName,
        keyId: registration.id,
        method: 'FIDO2 Pre-provisioning (Graph beta + WebAuthn)',
        message: 'FIDO2 security key provisioned and registered successfully',
        fido2Registration: {
          id: registration.id,
          aaGuid: registration.aaGuid,
          model: registration.model,
          attestationLevel: registration.attestationLevel,
          createdDateTime: registration.createdDateTime,
          authenticatorAttachment: webauthnResult.authenticatorAttachment,
        },
      };

      setResult(provisioningResult);

      // Step 4: Send mandatory security alert email
      setStatus('sending-alert');

      const alertDetails: SecurityAlertDetails = {
        timestamp,
        adminDisplayName: admin.displayName,
        adminUpn: admin.userPrincipalName,
        targetUserDisplayName: targetUser.displayName,
        targetUserUpn: targetUser.userPrincipalName,
        targetUserEmail: targetUser.mail || null,
        targetUserId: targetUser.id,
        keyDisplayName,
        method: 'FIDO2 Pre-provisioning (Graph beta + WebAuthn)',
        correlationId,
        tenantId: admin.tenantId,
        deviceInfo: navigator.userAgent,
        notes: notes || '',
        entraAdminUrl: `${ENTRA_ADMIN_USER_URL_PREFIX}${targetUser.id}`,
      };

      try {
        await sendSecurityAlertEmail(alertDetails);
        setAlertEmailSent(true);
      } catch (emailErr) {
        const msg = emailErr instanceof Error ? emailErr.message : 'Failed to send security alert';
        setEmailErrors((prev) => [...prev, `Security alert: ${msg}`]);
      }

      // Step 5: Optionally send PIN notification email
      if (sendPinEmail && pinRecipientEmail) {
        try {
          await sendPinNotificationEmail(
            pinRecipientEmail,
            pin,
            targetUser.displayName,
            keyDisplayName
          );
          setPinEmailSent(true);
        } catch (emailErr) {
          const msg = emailErr instanceof Error ? emailErr.message : 'Failed to send PIN email';
          setEmailErrors((prev) => [...prev, `PIN notification: ${msg}`]);
        }
      }

      setStatus('complete');
      onProvisioned?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Provisioning failed');
      setStatus('error');
    }
  }, [admin, targetUser, keyDisplayName, notes, pin, sendPinEmail, pinRecipientEmail, onProvisioned]);

  const isReady = pin.length >= 4 && keyDisplayName.trim().length > 0 && webauthnSupported;

  // ── Completed view ──────────────────────────────────────────────────────
  if (status === 'complete' && result) {
    return (
      <Card className={styles.card}>
        <div className={styles.successCard}>
          <div className={styles.successHeader}>
            <div className={styles.successIcon}>
              <CheckmarkCircleRegular fontSize={22} />
            </div>
            <div>
              <Text style={{ fontSize: '18px', fontWeight: 700, color: tokens.colorPaletteGreenForeground1 }} block>
                Provisioning Complete
              </Text>
              <Text style={{ fontSize: '13px', color: tokens.colorNeutralForeground2 }} block>
                FIDO2 security key has been provisioned and registered with Entra ID
              </Text>
            </div>
          </div>

          <div className={styles.resultDetails}>
            <div className={styles.resultRow}>
              <span className={styles.resultLabel}>Key ID</span>
              <span className={styles.resultValue}>{result.keyId}</span>
            </div>
            <div className={styles.resultRow}>
              <span className={styles.resultLabel}>Key Name</span>
              <span>{result.keyDisplayName}</span>
            </div>
            <div className={styles.resultRow}>
              <span className={styles.resultLabel}>User</span>
              <span>{result.targetUserDisplayName}</span>
            </div>
            {result.fido2Registration && (
              <>
                <div className={styles.resultRow}>
                  <span className={styles.resultLabel}>AAGUID</span>
                  <span className={styles.resultValue}>{result.fido2Registration.aaGuid}</span>
                </div>
                {result.fido2Registration.model && (
                  <div className={styles.resultRow}>
                    <span className={styles.resultLabel}>Key Model</span>
                    <span>{result.fido2Registration.model}</span>
                  </div>
                )}
                <div className={styles.resultRow}>
                  <span className={styles.resultLabel}>Attestation Level</span>
                  <Tag
                    size="extra-small"
                    shape="circular"
                    appearance="filled"
                    style={{
                      backgroundColor: result.fido2Registration.attestationLevel === 'attested'
                        ? tokens.colorPaletteGreenBackground2
                        : tokens.colorPaletteYellowBackground2,
                      color: result.fido2Registration.attestationLevel === 'attested'
                        ? tokens.colorPaletteGreenForeground2
                        : tokens.colorPaletteYellowForeground2,
                    }}
                  >
                    {result.fido2Registration.attestationLevel}
                  </Tag>
                </div>
              </>
            )}
            <div className={styles.resultRow}>
              <span className={styles.resultLabel}>Correlation ID</span>
              <span className={styles.resultValue}>{result.correlationId}</span>
            </div>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          <div className={styles.emailStatusRow}>
            {alertEmailSent ? (
              <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1 }} fontSize={16} />
            ) : (
              <DismissCircleRegular style={{ color: tokens.colorPaletteRedForeground1 }} fontSize={16} />
            )}
            <Text size={200}>
              Security alert → {SECURITY_ALERT_RECIPIENT}
            </Text>
            <Tag
              size="extra-small"
              shape="circular"
              appearance="filled"
              style={{
                backgroundColor: alertEmailSent ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2,
                color: alertEmailSent ? tokens.colorPaletteGreenForeground2 : tokens.colorPaletteRedForeground2,
              }}
            >
              {alertEmailSent ? 'Sent' : 'Failed'}
            </Tag>
          </div>

          {sendPinEmail && (
            <div className={styles.emailStatusRow}>
              {pinEmailSent ? (
                <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1 }} fontSize={16} />
              ) : (
                <DismissCircleRegular style={{ color: tokens.colorPaletteRedForeground1 }} fontSize={16} />
              )}
              <Text size={200}>
                PIN notification → {pinRecipientEmail}
              </Text>
              <Tag
                size="extra-small"
                shape="circular"
                appearance="filled"
                style={{
                  backgroundColor: pinEmailSent ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2,
                  color: pinEmailSent ? tokens.colorPaletteGreenForeground2 : tokens.colorPaletteRedForeground2,
                }}
              >
                {pinEmailSent ? 'Sent' : 'Failed'}
              </Tag>
            </div>
          )}

          {emailErrors.length > 0 && (
            <MessageBar intent="warning" style={{ marginTop: '12px' }}>
              <MessageBarBody>
                <MessageBarTitle>Email delivery issues</MessageBarTitle>
                {emailErrors.map((e, i) => (
                  <Text key={i} size={200} block>{e}</Text>
                ))}
              </MessageBarBody>
            </MessageBar>
          )}
        </div>

        <div className={styles.buttonRow} style={{ marginTop: '20px' }}>
          <Button appearance="primary" icon={<ArrowResetRegular />} onClick={onReset}>
            Provision another key
          </Button>
        </div>
      </Card>
    );
  }

  // ── Form view ───────────────────────────────────────────────────────────
  return (
    <Card className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.stepBadge}>3</div>
        <div>
          <Text className={styles.cardTitle} block>Provision FIDO2 Key</Text>
          <Text className={styles.cardSubtitle} block>
            Register a passkey on a USB security key for {targetUser.displayName}
          </Text>
        </div>
      </div>

      <div className={styles.form}>
        {/* Security Key Prerequisites */}
        <div className={styles.keyInfoBox}>
          <div className={styles.keyInfoHeader}>
            <UsbPlugRegular fontSize={16} />
            Security Key Requirements
          </div>
          <div className={styles.checkRow}>
            {webauthnSupported ? (
              <CheckmarkCircleRegular
                className={styles.checkIcon}
                fontSize={16}
                style={{ color: tokens.colorPaletteGreenForeground1 }}
              />
            ) : (
              <DismissCircleRegular
                className={styles.checkIcon}
                fontSize={16}
                style={{ color: tokens.colorPaletteRedForeground1 }}
              />
            )}
            <span style={{ color: webauthnSupported ? tokens.colorNeutralForeground1 : tokens.colorPaletteRedForeground1 }}>
              {webauthnSupported ? 'WebAuthn API available' : 'WebAuthn is not supported in this browser'}
            </span>
          </div>
          <div className={styles.checkRow}>
            <WarningRegular
              className={styles.checkIcon}
              fontSize={16}
              style={{ color: tokens.colorPaletteDarkOrangeForeground1 }}
            />
            <span>
              Have a USB security key ready — <strong>only external keys</strong> (USB/NFC/BLE) will be used
            </span>
          </div>
          <Text className={styles.keyInfoHint}>
            Windows Hello and other platform authenticators are excluded. The browser will prompt you
            to insert and touch a physical security key. The PIN you generated in step 2 will be set
            on the key during the ceremony.
          </Text>
        </div>

        <Field label="Key display name" required>
          <Input
            value={keyDisplayName}
            onChange={(_, data) => setKeyDisplayName(data.value)}
            disabled={isActive}
            appearance="filled-lighter"
          />
        </Field>

        <Field label="Admin notes (optional)">
          <Input
            value={notes}
            onChange={(_, data) => setNotes(data.value)}
            placeholder="e.g., Reason for provisioning, ticket number..."
            disabled={isActive}
            appearance="filled-lighter"
          />
        </Field>

        <div className={styles.notificationBox}>
          <div className={styles.notifHeader}>
            <MailRegular fontSize={16} />
            Email Notifications
          </div>
          <Text className={styles.notifHint}>
            A security alert will always be sent to <strong>{SECURITY_ALERT_RECIPIENT}</strong>.
          </Text>
          <Switch
            label="Also send PIN notification email"
            checked={sendPinEmail}
            onChange={(_, data) => setSendPinEmail(data.checked)}
            disabled={isActive}
          />
          {sendPinEmail && (
            <Field label="PIN recipient email">
              <Input
                type="email"
                value={pinRecipientEmail}
                onChange={(_, data) => setPinRecipientEmail(data.value)}
                disabled={isActive}
                appearance="filled-lighter"
              />
            </Field>
          )}
        </div>

        {/* Show progress substeps during provisioning */}
        {isActive && (() => {
          const steps = [
            { key: 'getting-options', label: 'Fetching challenge from Entra ID', icon: <CloudArrowUpRegular fontSize={16} /> },
            { key: 'waiting-for-key', label: 'Creating credential on security key', icon: <UsbPlugRegular fontSize={16} /> },
            { key: 'registering', label: 'Registering credential with Entra ID', icon: <ShieldKeyholeRegular fontSize={16} /> },
            { key: 'sending-alert', label: 'Sending notifications', icon: <MailRegular fontSize={16} /> },
          ];
          const statusOrder = ['getting-options', 'waiting-for-key', 'registering', 'sending-alert'];
          const currentIdx = statusOrder.indexOf(status);
          return (
            <div className={styles.progressBox}>
              {steps.map((step, i) => {
                const isDone = i < currentIdx;
                const isActiveStep = i === currentIdx;
                const styleClass = isDone
                  ? styles.progressDone
                  : isActiveStep
                    ? styles.progressActive
                    : styles.progressPending;
                return (
                  <div key={step.key} className={`${styles.progressStep} ${styleClass}`}>
                    <div className={styles.progressIcon}>
                      {isDone ? (
                        <CheckmarkCircleRegular fontSize={18} />
                      ) : isActiveStep ? (
                        <Spinner size="tiny" />
                      ) : (
                        step.icon
                      )}
                    </div>
                    <span>{step.label}</span>
                  </div>
                );
              })}
              {status === 'waiting-for-key' && (
                <div className={styles.touchPrompt}>
                  <UsbPlugRegular fontSize={20} />
                  Insert and touch your USB security key now. It may blink — tap it to confirm.
                </div>
              )}
            </div>
          );
        })()}

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Provisioning Failed</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.buttonRow}>
          <Button
            appearance="primary"
            className={styles.provisionBtn}
            icon={
              isActive
                ? <Spinner size="tiny" />
                : <SendRegular />
            }
            iconPosition="after"
            onClick={handleProvision}
            disabled={!isReady || isActive}
          >
            {STATUS_LABELS[status]}
          </Button>
        </div>
      </div>
    </Card>
  );
}
